// src/pages/Cart.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../utils/axiosInstance';

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [isCheckingOut, setIsCheckingOut] = useState(false); // برای غیرفعال کردن دکمه پرداخت
  const navigate = useNavigate();

  // بارگذاری سبد خرید و تنظیمات
  useEffect(() => {
    const loadCart = async () => {
      try {
        const savedCart = JSON.parse(localStorage.getItem('cart') || '[]');
        setCartItems(savedCart);

        const res = await axios.get('/settings');
        setSettings(res.data);
      } catch (error) {
        console.error('خطا در لود سبد خرید یا تنظیمات:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCart();

    // گوش دادن به تغییرات سبد خرید (از صفحه جزئیات محصول)
    const handleCartUpdate = () => {
      const savedCart = JSON.parse(localStorage.getItem('cart') || '[]');
      setCartItems(savedCart);
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate);
  }, []);

  const toToman = (toman) => Number(toman || 0).toLocaleString('fa-IR');

  const prePaymentPercent = parseFloat(settings.installment_pre_payment_percent || 30);
  const maxMonths = parseInt(settings.installment_max_months || 12, 10);

  const removeFromCart = (barcode) => {
    const newCart = cartItems.filter(item => item.product.barcode !== barcode);
    localStorage.setItem('cart', JSON.stringify(newCart));
    setCartItems(newCart);
    // اطلاع به هدر
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const updatePayment = (barcode, paymentType, months = 6) => {
    const newCart = cartItems.map(item =>
      item.product.barcode === barcode
        ? { ...item, paymentType, months: paymentType === 'installment' ? months : null }
        : item
    );
    localStorage.setItem('cart', JSON.stringify(newCart));
    setCartItems(newCart);
    window.dispatchEvent(new Event('cartUpdated'));
  };

  // تبدیل تاریخ میلادی به شمسی
  const toJalali = (gy, gm, gd) => {
    let jy, jm, jd;
    gy = gy || new Date().getFullYear();
    gm = gm || new Date().getMonth() + 1;
    gd = gd || new Date().getDate();

    if (gy > 1600) {
      let gy2 = gy - 1600;
      let gm2 = gm - 1;
      let gDayNo = 365 * gy2 + Math.floor((gy2 + 3) / 4) - Math.floor((gy2 + 99) / 100) + Math.floor((gy2 + 399) / 400);
      for (let i = 0; i < gm2; ++i) gDayNo += [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][i];
      if (gm > 2 && ((gy2 % 4 === 0 && gy2 % 100 !== 0) || gy2 % 400 === 0)) gDayNo++;
      gDayNo += gd - 1;

      let jDayNo = gDayNo - 79;
      let jNp = Math.floor(jDayNo / 12053);
      jDayNo %= 12053;

      jy = 979 + 33 * jNp + 4 * Math.floor(jDayNo / 1461);
      jDayNo %= 1461;

      if (jDayNo >= 366) {
        jy += Math.floor((jDayNo - 1) / 365);
        jDayNo = (jDayNo - 1) % 365;
      }

      jm = jDayNo < 186 ? 1 + Math.floor(jDayNo / 31) : 7 + Math.floor((jDayNo - 186) / 30);
      jd = 1 + (jDayNo < 186 ? jDayNo % 31 : (jDayNo - 186) % 30);
    } else {
      jy = -14;
      jm = gm + 11;
      jd = gd;
    }

    return { year: jy, month: jm, day: jd };
  };

  // محاسبه جمع کل
  let totalPrePayment = 0;
  let totalGoldDebt = 0;

  cartItems.forEach(item => {
    const finalPrice = Number(item.product.final_price) || 0;
    const beforeDiscountPrice = Number(item.product.price_before_discount) || 0;
    const currentGoldPrice = Number(item.product.current_gold_price) || 1;

    const price = item.paymentType === 'cash' ? finalPrice : beforeDiscountPrice;

    if (item.paymentType === 'cash') {
      totalPrePayment += price;
    } else {
      const prePayment = Math.round(price * (prePaymentPercent / 100));
      const remaining = price - prePayment;
      const goldWeight = remaining / currentGoldPrice;

      totalPrePayment += prePayment;
      totalGoldDebt += goldWeight;
    }
  });

  const isLoggedIn = () => {
    const authData = localStorage.getItem('auth-storage');
    if (!authData) return false;

    try {
      const parsed = JSON.parse(authData);
      return parsed.state?.isLoggedIn === true;
    } catch {
      return false;
    }
  };

  const handleCheckout = async () => {
    if (isCheckingOut) return; // جلوگیری از کلیک چندباره

    if (!isLoggedIn()) {
      if (window.confirm('برای پرداخت باید وارد حساب شوید. به صفحه ورود برویم؟')) {
        navigate('/login', { state: { from: '/cart' } });
      }
      return;
    }

    setIsCheckingOut(true); // غیرفعال کردن دکمه

    try {
      const orderRes = await axios.post('/orders/create', { cart: cartItems });

      if (orderRes.data.success) {
        const order = orderRes.data.order;

        // شبیه‌سازی پرداخت
        const testRes = await axios.post('/payment/test/success', { orderId: order.id });

        if (testRes.data.success) {
          // پاک کردن سبد خرید بعد از پرداخت موفق
          localStorage.removeItem('cart');
          window.dispatchEvent(new Event('cartUpdated'));

          navigate(`/payment/success?orderId=${order.id}&refId=${testRes.data.refId}`);
        } else {
          alert(testRes.data.message || 'خطا در شبیه‌سازی پرداخت');
        }
      }
    } catch (error) {
      console.error('خطا در ثبت سفارش:', error);
      alert(error.response?.data?.message || 'خطا در ثبت سفارش');
    } finally {
      setIsCheckingOut(false); // دوباره فعال کردن دکمه (در صورت خطا)
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-2xl text-gold-dark">در حال بارگذاری...</div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-20 text-center">
        <p className="text-3xl text-gray-600 mb-10">سبد خرید شما خالی است</p>
        <Link to="/products" className="bg-gold hover:bg-gold-dark text-white font-bold py-4 px-12 rounded-xl text-xl transition shadow-lg">
          ادامه خرید
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold text-center text-gold-dark mb-10">سبد خرید</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* لیست محصولات */}
          <div className="lg:col-span-2 space-y-8">
            {cartItems.map(item => {
              const finalPrice = Number(item.product.final_price) || 0;
              const beforeDiscountPrice = Number(item.product.price_before_discount) || 0;
              const currentGoldPrice = Number(item.product.current_gold_price) || 1;

              const price = item.paymentType === 'cash' ? finalPrice : beforeDiscountPrice;

              const prePayment = item.paymentType === 'installment' ? Math.round(price * (prePaymentPercent / 100)) : 0;
              const remaining = item.paymentType === 'installment' ? price - prePayment : 0;
              const goldDebt = item.paymentType === 'installment' ? remaining / currentGoldPrice : 0;
              const monthlyGold = item.months > 0 ? goldDebt / item.months : 0;

              return (
                <div key={item.product.barcode} className="bg-white rounded-2xl shadow-md overflow-hidden">
                  <div className="flex flex-col lg:flex-row">
                    <div className="lg:w-1/3">
                      <img
                        src={item.product.images?.[0]?.image_url || '/placeholder.jpg'}
                        alt={item.product.name}
                        className="w-full h-64 lg:h-full object-cover"
                      />
                    </div>

                    <div className="flex-1 p-6 lg:p-8">
                      <div className="flex justify-between items-start mb-6">
                        <div>
                          <h3 className="text-xl font-bold text-gray-800">{item.product.name || 'محصول بدون نام'}</h3>
                          <p className="text-gray-600 mt-1">بارکد: {item.product.barcode}</p>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.product.barcode)}
                          className="text-red-500 hover:text-red-700 text-sm font-medium"
                        >
                          حذف
                        </button>
                      </div>

                      <p className="text-gray-700 mb-6">
                        تعداد: <span className="font-bold text-gold-dark">۱</span> عدد
                      </p>

                      {/* روش پرداخت */}
                      <div className="mb-8">
                        <p className="text-gray-800 font-medium mb-3">روش پرداخت</p>
                        <div className="flex gap-8">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              checked={item.paymentType === 'cash'}
                              onChange={() => updatePayment(item.product.barcode, 'cash')}
                              className="mr-3"
                            />
                            <span className="text-gray-700">نقدی</span>
                          </label>
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              checked={item.paymentType === 'installment'}
                              onChange={() => updatePayment(item.product.barcode, 'installment')}
                              className="mr-3"
                            />
                            <span className="text-gray-700">اقساطی</span>
                          </label>
                        </div>
                      </div>

                      {item.paymentType === 'installment' && (
                        <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                          <label className="block text-gray-700 font-medium mb-3">تعداد قسط</label>
                          <select
                            value={item.months || 6}
                            onChange={(e) => updatePayment(item.product.barcode, 'installment', Number(e.target.value))}
                            className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:border-purple-500 focus:outline-none"
                          >
                            {[...Array(maxMonths)].map((_, i) => (
                              <option key={i + 1} value={i + 1}>{i + 1} قسط</option>
                            ))}
                          </select>

                          <div className="grid grid-cols-3 gap-4 mt-6 text-center">
                            <div>
                              <p className="text-sm text-gray-600">پیش‌پرداخت</p>
                              <p className="font-bold text-purple-800">{toToman(prePayment)} تومان</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">بدهی کل</p>
                              <p className="font-bold text-purple-900">{goldDebt.toFixed(3)} گرم</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">هر قسط</p>
                              <p className="font-bold text-purple-900">{monthlyGold.toFixed(3)} گرم</p>
                            </div>
                          </div>

                          {/* جدول اقساط */}
                          <div className="mt-6">
                            <p className="text-center text-purple-900 font-medium mb-3">جدول پرداخت اقساط</p>
                            <div className="overflow-x-auto max-h-64">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-purple-100">
                                    <th className="p-2 border border-purple-200">قسط</th>
                                    <th className="p-2 border border-purple-200">تاریخ سررسید</th>
                                    <th className="p-2 border border-purple-200">وزن طلا</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(() => {
                                    const today = new Date();
                                    const list = [];
                                    for (let i = 1; i <= item.months; i++) {
                                      const dueDate = new Date(today);
                                      dueDate.setMonth(today.getMonth() + i);
                                      const j = toJalali(dueDate.getFullYear(), dueDate.getMonth() + 1, dueDate.getDate());
                                      list.push({
                                        number: i,
                                        date: `${j.year}/${String(j.month).padStart(2, '0')}/${String(j.day).padStart(2, '0')}`,
                                        weight: monthlyGold.toFixed(3)
                                      });
                                    }
                                    return list.map(l => (
                                      <tr key={l.number} className="hover:bg-purple-50">
                                        <td className="p-2 text-center border border-purple-100">{l.number}</td>
                                        <td className="p-2 text-center border border-purple-100">{l.date}</td>
                                        <td className="p-2 text-center border border-purple-100">{l.weight} گرم</td>
                                      </tr>
                                    ));
                                  })()}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="mt-8 pt-6 border-t border-gray-200 flex justify-between items-end">
                        <div>
                          <p className="text-2xl font-bold text-gold-dark">{toToman(price)} تومان</p>
                          {item.paymentType === 'installment' && (
                            <p className="text-sm text-gray-500 mt-1">قیمت بدون تخفیف</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* خلاصه سفارش */}
          <div className="lg:sticky lg:top-8 h-fit">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gold-dark text-center mb-8">خلاصه سفارش</h3>

              <div className="space-y-6">
                <div className="flex justify-between py-4 border-b">
                  <span className="text-gray-700">جمع پیش‌پرداخت:</span>
                  <span className="font-bold text-xl">{toToman(totalPrePayment)} تومان</span>
                </div>

                {totalGoldDebt > 0 && (
                  <div className="bg-purple-50 rounded-xl p-6 text-center">
                    <p className="text-gray-700 mb-2">جمع بدهی به گرم طلا:</p>
                    <p className="text-3xl font-bold text-purple-900">{totalGoldDebt.toFixed(3)} گرم</p>
                  </div>
                )}
              </div>

              <button
                onClick={handleCheckout}
                disabled={isCheckingOut || cartItems.length === 0}
                className={`w-full mt-10 font-bold py-4 rounded-xl text-lg transition shadow-md ${
                  isCheckingOut
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {isCheckingOut ? 'در حال پردازش...' : 'پرداخت پیش‌پرداخت'}
              </button>

              <p className="text-center text-sm text-gray-600 mt-6">
                پس از پرداخت، فاکتور اقساطی صادر می‌شود
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;